import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { Router } from '@angular/router';

import { finalize } from 'rxjs/operators';
import { AgricolaService } from '../../../services/agricola.service';

// ── API response types ────────────────────────────────────────────────────────

export interface ApiPolicy {
  id: number;
  code: string;
  status: number;
  date_canceled: string | null;
  cancellation_reason: string;
  has_addons?: boolean;
  total_with_addons?: number;
  member: {
    id: number;
    name: string;
    national_id: string;
    mobile: string | null;
    phone1: string | null;
    email: string | null;
  };
  plan: {
    id: number;
    name: string;
    base_price: number;
    currency: { symbol: string };
    product: { assistance_code: string; name: string };
  };
}

// ── Internal view model ───────────────────────────────────────────────────────

export interface PolicyResult {
  id: number;
  status: string;
  policyNumber: string;
  clientName: string;
  productType: string;
  productCode: string;
  productName: string;
  currencySymbol: string;
  insuredAmount: number;
}

// ── Component ─────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-agricola-consultas',
  templateUrl: './agricola-consultas.component.html',
  styleUrls: ['./agricola-consultas.component.css'],
  standalone: false
})
export class AgricolaConsultasComponent implements OnInit {

  searchForm: FormGroup;
  isLoading = false;
  hasSearched = false;
  showToast = false;
  toastMessage = '';
  toastType: 'error' | 'success' | 'warning' | 'info' = 'error';

  searchTypes = [
    { value: 10, label: 'Buscar por no. de id nacional' },
    { value: 9,  label: 'Buscar por nombre' },
    { value: 11, label: 'Buscar por teléfono' },
    { value: 12, label: 'Buscar por código de asistencia' }
  ];

  pagedResults: PolicyResult[] = [];

  totalItems = 0;
  currentPage = 1;
  pageSize = 15;
  pageSizeOptions = [5, 10, 15, 25, 50];

  get totalPages(): number {
    return Math.ceil(this.totalItems / this.pageSize);
  }

  get visiblePages(): number[] {
    const pages: number[] = [];
    const total = this.totalPages;
    const current = this.currentPage;

    let start = Math.max(1, current - 1);
    const end = Math.min(total, start + 3);
    if (end - start < 3) start = Math.max(1, end - 3);

    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  get movementsLabel(): string {
    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.totalItems);
    return `Mostrando ${end - start + 1} movimientos de ${this.totalItems}`;
  }

  constructor(
    private fb: FormBuilder,
    private location: Location,
    private router: Router,
    private agricolaService: AgricolaService
  ) {
    this.searchForm = this.fb.group({
      searchType:   [10, Validators.required],
      searchValue:  ['', Validators.required]
    });
  }

  ngOnInit(): void {}

  onSearch(): void {
    if (this.searchForm.invalid) return;
    this.currentPage = 1;
    // Una búsqueda nueva no muestra los resultados de la anterior mientras carga.
    this.pagedResults = [];
    this.totalItems = 0;
    this.fetchResults();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.fetchResults();
  }

  onPageSizeChange(event: Event): void {
    this.pageSize = Number((event.target as HTMLSelectElement).value);
    this.currentPage = 1;
    this.fetchResults();
  }

  onRowClick(result: PolicyResult): void {
    this.router.navigate(['/agricola/policy-detail', result.id]);
  }

  hideToast(): void {
    this.showToast = false;
  }

  goBack(): void {
    this.location.back();
  }

  private fetchResults(): void {
    const { searchType, searchValue } = this.searchForm.value;
    this.isLoading = true;
    this.showToast = false;

    this.agricolaService.searchPolicies(searchValue, this.currentPage, this.pageSize, Number(searchType))
      .pipe(finalize(() => {
        this.isLoading = false;
        this.hasSearched = true;
      }))
      .subscribe({
        next: (response: any) => {
          const raw = Array.isArray(response) ? response : (response?.data ?? response);
          const items: ApiPolicy[] = Array.isArray(raw?.[0]) ? raw[0] : [];
          const total: number = typeof raw?.[1] === 'number' ? raw[1] : items.length;
          this.pagedResults = items.map(p => this.mapFromApi(p));
          this.totalItems = total;
        },
        error: () => {
          this.pagedResults = [];
          this.totalItems = 0;
          this.toastMessage = 'Error al consultar las pólizas. Intenta nuevamente.';
          this.toastType = 'error';
          this.showToast = true;
        }
      });
  }

  private mapFromApi(item: ApiPolicy): PolicyResult {
    const isCancelled = item.status !== 1 || !!item.date_canceled;
    return {
      id:             item.id,
      status:         isCancelled ? 'CANCELADA' : 'ACTIVA',
      policyNumber:   item.code,
      clientName:     item.member.name,
      productType:    item.plan.name   ,
      productCode:    item.plan.product.assistance_code,
      productName:    item.plan.product.name,
      currencySymbol: item.plan.currency.symbol,
      insuredAmount:  item.has_addons ? (item.total_with_addons ?? item.plan.base_price) : item.plan.base_price
    };
  }
}
